/**
 * Superboba Cart Drawer
 *
 * Custom elements:
 *   <cart-drawer>       — drawer open/close/render (compatible with product-form.js)
 *   <cart-drawer-items> — extends CartItems for quantity/remove (compatible with cart.js)
 *
 * Additional handlers:
 *   - Per-item subscription upgrade buttons
 *   - Upsell quick-add buttons
 *   - Header cart count sync for sb-header
 */

/* ──────────────────────────────────────────────
   CartDrawer — main drawer element
   ────────────────────────────────────────────── */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Close on click outside the panel — any click within <cart-drawer> that
    // is NOT inside the panel triggers close. This survives innerHTML replacements
    // because the handler is on the custom element itself (never replaced).
    this.addEventListener('click', (e) => {
      if (!e.target.closest('.sb-cart-drawer__panel')) {
        this.close();
      }
    });

    this.bindCartIcons();
    this._rebindAll();
  }

  /* ── Cart Icon Binding ── */

  bindCartIcons() {
    const dawnIcon = document.querySelector('#cart-icon-bubble');
    if (dawnIcon) this._bindIcon(dawnIcon);

    const sbIcon = document.querySelector('#sb-cart-icon');
    if (sbIcon) this._bindIcon(sbIcon);

    /* sb-header icon is also #sb-cart-icon — covered above */
  }

  _bindIcon(el) {
    el.setAttribute('role', 'button');
    el.setAttribute('aria-haspopup', 'dialog');
    el.addEventListener('click', (e) => {
      e.preventDefault();
      this.open(el);
    });
    el.addEventListener('keydown', (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        this.open(el);
      }
    });
  }

  /* ── Open / Close ── */

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.sb-cart-drawer__empty')
          : document.getElementById('CartDrawer');
        const focusEl = this.querySelector('.sb-cart-drawer__panel') || this.querySelector('.sb-cart-drawer__close');
        trapFocus(container, focusEl);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Reset bundle builder selections so user sees a fresh state
    document.querySelectorAll('.bundle-product-list-item.item-active').forEach(function(card) {
      card.classList.remove('item-active');
      var qtyBox = card.querySelector('.bundle-quantity-box');
      var addBtn = card.querySelector('.bundle-add-btn');
      var qtyDisplay = card.querySelector('.bundle-qty-display');
      if (qtyBox) qtyBox.classList.remove('is-active');
      if (addBtn) addBtn.classList.add('is-active');
      if (qtyDisplay) qtyDisplay.textContent = '0';
    });
    document.dispatchEvent(new CustomEvent('bundle:reset'));
  }

  /* ── Render Contents (called by product-form.js after add-to-cart) ── */

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Header counts + Loop bundles (single /cart.json fetch) and handler re-bind
    this._syncCartState();
    this._rebindAll();

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'sb-cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ── Fetch & Open (called by bundle_products.js after add-to-cart) ── */

  fetchAndOpen() {
    fetch(`${routes.cart_url}?section_id=sb-cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('#CartDrawer');
        const target = document.querySelector('#CartDrawer');
        if (source && target) {
          target.innerHTML = source.innerHTML;
        }

        this.classList.remove('is-empty');
        const drawerItems = this.querySelector('cart-drawer-items');
        if (drawerItems) drawerItems.classList.remove('is-empty');

        this._syncCartState();
        this._rebindAll();

        this.open();
      })
      .catch((e) => console.error('fetchAndOpen error:', e));
  }

  /* ── Rebind all interactive handlers (after any innerHTML swap) ── */

  _rebindAll() {
    this.bindUpgradeButtons();
    this.bindUpsellButtons();
    this.bindUpsellCarousel();
  }

  /* ── Cart State Sync (header counts + Loop bundles) ──
     Pass a cart object (from a /cart/change.js response) to skip the
     network round-trip entirely; otherwise ONE /cart.json fetch feeds
     both the header badge and the Loop bundle re-init. */

  _syncCartState(cart) {
    if (cart && typeof cart.item_count === 'number' && Array.isArray(cart.items)) {
      this._applyCartState(cart);
      return;
    }
    fetch('/cart.json', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => this._applyCartState(data))
      .catch(() => {});
  }

  _applyCartState(cart) {
    this._setHeaderCount(cart.item_count);
    if (typeof initLoopBundle === 'function') {
      window.Loop = window.Loop || {};
      window.Loop.bundleCartAllItems = cart.items;
      initLoopBundle('LOOP_aaebd9-18_bundles_drawer');
    }
  }

  _setHeaderCount(count) {
    // sb-header count badge
    const sbCount = document.querySelector('.sb-header__cart-count');
    if (sbCount) {
      sbCount.textContent = count;
      sbCount.style.display = count > 0 ? '' : 'none';
    }

    // sb-header count badge
    const sbHeaderCount = document.querySelector('.sb-header__cart-count');
    if (sbHeaderCount) {
      sbHeaderCount.textContent = count;
      sbHeaderCount.style.display = count > 0 ? '' : 'none';
    }

    // dawn header count (if present)
    const dawnBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span');
    if (dawnBubble) {
      dawnBubble.textContent = count;
    }

    // drawer title bag badge (lives in the header region, which quantity
    // updates don't re-render — keep it in sync here)
    const bagCount = this.querySelector('.sb-cart-drawer__title-bag-count');
    if (bagCount) {
      bagCount.textContent = count;
      bagCount.style.display = count > 0 ? '' : 'none';
    }
  }

  /* ── Per-Item Subscription Upgrade ── */

  bindUpgradeButtons() {
    this.querySelectorAll('.sb-cart-drawer__upgrade-btn').forEach((btn) => {
      if (btn._bound) return;
      btn._bound = true;

      btn.addEventListener('click', async () => {
        if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;

        const variantId = parseInt(btn.dataset.variantId);
        const sellingPlanId = parseInt(btn.dataset.sellingPlanId);
        const itemIndex = parseInt(btn.dataset.itemIndex);
        const itemQuantity = parseInt(btn.dataset.itemQuantity);

        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;

        try {
          // Step 1: Remove the one-time item
          await fetch(routes.cart_change_url, {
            ...fetchConfig(),
            body: JSON.stringify({ line: itemIndex, quantity: 0 }),
          });

          // Step 2: Add back with selling plan + re-render
          const addResponse = await fetch(routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [{ id: variantId, quantity: itemQuantity, selling_plan: sellingPlanId }],
              sections: this.getSectionsToRender().map((s) => s.id),
            }),
          });

          const addResult = await addResponse.json();

          // Re-render drawer with sections from add response
          if (addResult.sections) {
            this.getSectionsToRender().forEach((section) => {
              const el = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              if (!el || !addResult.sections[section.id]) return;
              el.innerHTML = this.getSectionInnerHTML(addResult.sections[section.id], section.selector);
            });
          }

          this._syncCartState();
          this._rebindAll();

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: addResult });
        } catch (err) {
          console.error('Upgrade to subscription error:', err);
          try {
            await fetch(routes.cart_add_url, {
              ...fetchConfig(),
              body: JSON.stringify({ items: [{ id: variantId, quantity: itemQuantity }] }),
            });
          } catch (recoveryErr) {
            console.error('Recovery failed:', recoveryErr);
          }
          window.location.href = routes.cart_url;
        }
      });
    });
  }

  /* ── Upsell Quick-Add ── */

  bindUpsellButtons() {
    this.querySelectorAll('.sb-cart-drawer__upsell-add, .sb-cart-drawer__featured-upsell-cta, .sb-cart-drawer__empty-card-add').forEach((btn) => {
      if (btn._bound) return;
      btn._bound = true;

      btn.addEventListener('click', async () => {
        if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;

        const variantId = parseInt(btn.dataset.variantId);
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;

        // Upsell and featured-upsell cards are one-time add-ons. Mark the line
        // with the `_addon` property, the same way the PDP add-on card does
        // (`add-on-page`), so Sinensis can count add-on purchases per surface.
        // The empty-cart product cards are not add-ons and stay unmarked.
        const item = { id: variantId, quantity: 1 };
        if (!btn.classList.contains('sb-cart-drawer__empty-card-add')) {
          item.properties = { _addon: 'add-on-cart' };
        }

        try {
          const response = await fetch(routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: [item],
              sections: this.getSectionsToRender().map((s) => s.id),
            }),
          });

          const result = await response.json();

          if (result.sections) {
            this.getSectionsToRender().forEach((section) => {
              const el = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              if (!el || !result.sections[section.id]) return;
              el.innerHTML = this.getSectionInnerHTML(result.sections[section.id], section.selector);
            });
          }

          // Drawer might have been empty — remove is-empty class
          this.classList.remove('is-empty');
          const drawerItems = this.querySelector('cart-drawer-items');
          if (drawerItems) drawerItems.classList.remove('is-empty');

          this._syncCartState();
          this._rebindAll();
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: result });
        } catch (err) {
          console.error('Upsell add error:', err);
          btn.removeAttribute('aria-busy');
          btn.disabled = false;
        }
      });
    });
  }
  /* ── Upsell Carousel (dots + mouse drag) ── */

  bindUpsellCarousel() {
    var track = this.querySelector('.sb-cart-drawer__upsell-track');
    var dots = this.querySelectorAll('.sb-cart-drawer__upsell-dot');
    if (!track || track._dragBound) return;
    track._dragBound = true;

    /* Dot navigation */
    dots.forEach(function(dot) {
      dot.addEventListener('click', function() {
        var idx = parseInt(this.dataset.slide, 10);
        var slide = track.children[idx];
        if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
    });

    /* Dot updates on scroll */
    if (dots.length > 1) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            var idx = Array.from(track.children).indexOf(entry.target);
            dots.forEach(function(d, i) { d.classList.toggle('is-active', i === idx); });
          }
        });
      }, { root: track, threshold: 0.6 });
      Array.from(track.children).forEach(function(slide) { observer.observe(slide); });
    }

    /* Mouse drag to scroll */
    var isDown = false, startX, scrollLeft;
    track.addEventListener('mousedown', function(e) {
      e.preventDefault();
      isDown = true;
      track.classList.add('is-dragging');
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    });
    track.addEventListener('mouseleave', function() {
      if (isDown) { isDown = false; track.classList.remove('is-dragging'); }
    });
    track.addEventListener('mouseup', function() {
      isDown = false;
      track.classList.remove('is-dragging');
    });
    track.addEventListener('mousemove', function(e) {
      if (!isDown) return;
      e.preventDefault();
      var x = e.pageX - track.offsetLeft;
      track.scrollLeft = scrollLeft - (x - startX);
    });
  }

}

customElements.define('cart-drawer', CartDrawer);

/* ──────────────────────────────────────────────
   CartDrawerItems — extends CartItems for quantity + remove
   ────────────────────────────────────────────── */

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'sb-cart-drawer',
        selector: '.sb-cart-drawer__panel',
      },
    ];
  }

  /**
   * Override Dawn's updateQuantity to properly re-render drawer elements
   * without replacing the cart-drawer-items custom element itself.
   * Dawn's default replaces .sb-cart-drawer__panel innerHTML, which destroys
   * this element and leaves the old instance's promise chain orphaned.
   */
  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: ['sb-cart-drawer'],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Update empty state
        const cartDrawer = document.querySelector('cart-drawer');
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawer) cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);

        // Re-render drawer inner elements via innerHTML (keeps custom element alive)
        if (parsedState.sections['sb-cart-drawer']) {
          const html = new DOMParser().parseFromString(parsedState.sections['sb-cart-drawer'], 'text/html');
          const selectors = [
            { sel: '#CartDrawer-CartItems', attr: 'innerHTML' },
            { sel: '.sb-cart-drawer__footer', attr: 'innerHTML' },
            { sel: '.sb-cart-drawer__shipping-bar', attr: 'innerHTML' },
            { sel: '.sb-cart-drawer__upsells', attr: 'outerHTML' },
          ];
          for (const { sel, attr } of selectors) {
            const target = document.querySelector(sel);
            const source = html.querySelector(sel);
            if (target && source) {
              if (attr === 'outerHTML') {
                target.outerHTML = source.outerHTML;
              } else {
                target.innerHTML = source.innerHTML;
              }
            } else if (!source && target && sel === '.sb-cart-drawer__upsells') {
              target.remove();
            }
          }
        }

        // Re-bind handlers on new DOM; parsedState is a full cart payload,
        // so state sync needs no extra network round-trip
        if (cartDrawer) {
          cartDrawer._rebindAll();
          cartDrawer._syncCartState(parsedState);
        }

        // Focus management
        const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name) {
          const focusEl = lineItem.querySelector(`[name="${name}"]`);
          if (focusEl) focusEl.focus();
        } else if (parsedState.item_count === 0 && cartDrawer) {
          const emptyArea = cartDrawer.querySelector('.sb-cart-drawer__empty');
          const emptyLink = cartDrawer.querySelector('.sb-cart-drawer__empty-cta');
          if (emptyArea && emptyLink) trapFocus(cartDrawer, emptyLink);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });

        // Auto-swap delivery frequency if bag count crossed a tier
        this._autoSwapFrequency(parsedState);
      })
      .catch((err) => {
        console.error('Cart drawer quantity update error:', err);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings?.error || 'Something went wrong. Please try again.';
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /**
   * Auto-swap selling plan frequency when subscription bag count crosses tiers.
   * 1-2 bags = 30 days, 3-4 bags = 60 days, 5+ bags = 90 days.
   * Clears cart and re-adds all items with the correct selling plan.
   */
  async _autoSwapFrequency(cartData) {
    const PLAN_30 = 4573528164;
    const PLAN_60 = 4573593700;
    const PLAN_90 = 4582408292;
    const ALL_PLANS = [PLAN_30, PLAN_60, PLAN_90];

    var subItems = [];
    var totalSubQty = 0;

    for (var i = 0; i < cartData.items.length; i++) {
      var item = cartData.items[i];
      if (item.properties && item.properties._bundleId) continue;
      if (!item.selling_plan_allocation) continue;
      var planId = item.selling_plan_allocation.selling_plan.id;
      if (ALL_PLANS.indexOf(planId) === -1) continue;
      subItems.push(item);
      totalSubQty += item.quantity;
    }

    if (subItems.length === 0) return;

    var targetPlan = PLAN_30;
    if (totalSubQty >= 5) targetPlan = PLAN_90;
    else if (totalSubQty >= 3) targetPlan = PLAN_60;

    var needsSwap = false;
    for (var j = 0; j < subItems.length; j++) {
      if (subItems[j].selling_plan_allocation.selling_plan.id !== targetPlan) {
        needsSwap = true;
        break;
      }
    }
    if (!needsSwap) return;

    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      var addItems = [];
      for (var k = 0; k < cartData.items.length; k++) {
        var it = cartData.items[k];
        var entry = { id: it.variant_id, quantity: it.quantity };

        if (it.properties && Object.keys(it.properties).length > 0) {
          entry.properties = it.properties;
        }

        if (it.selling_plan_allocation) {
          var curPlan = it.selling_plan_allocation.selling_plan.id;
          var isBundleChild = it.properties && it.properties._bundleId;
          if (ALL_PLANS.indexOf(curPlan) !== -1 && !isBundleChild) {
            entry.selling_plan = targetPlan;
          } else {
            entry.selling_plan = curPlan;
          }
        }

        addItems.push(entry);
      }

      await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({ items: addItems }),
      });

      this.onCartUpdate();
    } catch (err) {
      console.error('Frequency auto-swap error:', err);
      this.onCartUpdate();
    }
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=sb-cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = [
          { sel: '#CartDrawer-CartItems', attr: 'innerHTML' },
          { sel: '.sb-cart-drawer__footer', attr: 'innerHTML' },
          { sel: '.sb-cart-drawer__shipping-bar', attr: 'innerHTML' },
          { sel: '.sb-cart-drawer__upsells', attr: 'outerHTML' },
        ];
        for (const { sel, attr } of selectors) {
          const target = document.querySelector(sel);
          const source = html.querySelector(sel);
          if (target && source) {
            if (attr === 'outerHTML') {
              target.outerHTML = source.outerHTML;
            } else {
              target.innerHTML = source.innerHTML;
            }
          }
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer._rebindAll();
          drawer._syncCartState();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ──────────────────────────────────────────────
   Document-level cart icon handler (capture phase — bulletproof)
   ────────────────────────────────────────────── */
document.addEventListener(
  'click',
  (e) => {
    const icon = e.target.closest('#sb-cart-icon, #cart-icon-bubble');
    if (!icon) return;
    e.preventDefault();
    e.stopPropagation();
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.open(icon);
  },
  true
);
